import { FormEvent, useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { AdminAlert } from "@/components/shell/AdminPage";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  OTPField,
  OTPFieldInput,
  OTPFieldSeparator,
} from "@/components/ui/otp-field";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  confirmNewEmail,
  sendCurrentEmailOtp,
  sendNewEmailOtp,
  verifyCurrentEmail,
} from "@/lib/me-api";
import { cn } from "@/lib/utils";

type Step = "intro" | "current_code" | "new_email" | "new_code" | "done";

type EmailChangePanelProps = {
  currentEmail: string;
  onClose: () => void;
};

export function EmailChangePanel({ currentEmail, onClose }: EmailChangePanelProps) {
  const { applySession } = useAuth();
  const [step, setStep] = useState<Step>("intro");
  const [newEmail, setNewEmail] = useState("");
  const [currentCode, setCurrentCode] = useState("");
  const [newCode, setNewCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  function handleClose() {
    setStep("intro");
    setNewEmail("");
    setCurrentCode("");
    setNewCode("");
    setFieldError(null);
    setIsSubmitting(false);
    setResendCooldown(0);
    onClose();
  }

  async function handleSendCurrent(force = false) {
    setFieldError(null);
    setIsSubmitting(true);
    try {
      const result = await sendCurrentEmailOtp(force ? { force: true } : undefined);
      setResendCooldown(result.cooldown_seconds ?? 0);
      setCurrentCode("");
      setStep("current_code");
      toast.message(result.message || (result.code_sent_recently ? "验证码仍有效" : "验证码已发送"));
    } catch (error) {
      setFieldError(error instanceof ApiError ? error.message : "发送失败，请稍后重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVerifyCurrent(event?: FormEvent, codeOverride?: string) {
    event?.preventDefault();
    const code = codeOverride ?? currentCode;
    setFieldError(null);
    setIsSubmitting(true);
    try {
      await verifyCurrentEmail(code);
      setStep("new_email");
      toast.success("当前邮箱已验证");
    } catch (error) {
      setFieldError(error instanceof ApiError ? error.message : "验证码无效，请重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSendNew(event: FormEvent) {
    event.preventDefault();
    setFieldError(null);
    setIsSubmitting(true);
    try {
      const result = await sendNewEmailOtp(newEmail.trim());
      setResendCooldown(result.cooldown_seconds ?? 0);
      setNewCode("");
      setStep("new_code");
      toast.message(result.message || "验证码已发送至新邮箱");
    } catch (error) {
      setFieldError(error instanceof ApiError ? error.message : "发送失败，请稍后重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleConfirmNew(event?: FormEvent, codeOverride?: string) {
    event?.preventDefault();
    const code = codeOverride ?? newCode;
    setFieldError(null);
    setIsSubmitting(true);
    try {
      const session = await confirmNewEmail(newEmail.trim(), code);
      applySession(session);
      setStep("done");
      toast.success("邮箱已更新，其他设备需重新登录");
    } catch (error) {
      setFieldError(error instanceof ApiError ? error.message : "确认失败，请稍后重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  const stepLabels = ["验证当前", "填写新邮箱", "验证新邮箱", "完成"];

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Mail className="size-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">更换登录邮箱</p>
          <p className="text-sm text-muted-foreground">
            分四步完成：验证当前邮箱 → 填写新邮箱 → 验证新邮箱 → 自动刷新登录。
          </p>
        </div>
      </div>

      <ol className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {stepLabels.map((label, index) => (
          <li
            key={label}
            className={cn(
              "rounded-full border px-2.5 py-1",
              (step === "intro" && index === 0) ||
                (step === "current_code" && index === 0) ||
                (step === "new_email" && index === 1) ||
                (step === "new_code" && index === 2) ||
                (step === "done" && index === 3)
                ? "border-primary/40 bg-primary/5 text-foreground"
                : "border-border",
            )}
          >
            {index + 1}. {label}
          </li>
        ))}
      </ol>

      {fieldError ? <AdminAlert tone="error">{fieldError}</AdminAlert> : null}

      {step === "intro" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            当前邮箱：<span className="font-medium text-foreground">{currentEmail}</span>
          </p>
          <Button type="button" disabled={isSubmitting} onClick={() => void handleSendCurrent()}>
            {isSubmitting ? "发送中…" : "发送验证码到当前邮箱"}
          </Button>
        </div>
      )}

      {step === "current_code" && (
        <form className="space-y-4" onSubmit={handleVerifyCurrent}>
          <p className="text-sm text-muted-foreground">请输入发送至 {currentEmail} 的 6 位验证码</p>
          <OtpInput
            value={currentCode}
            disabled={isSubmitting}
            invalid={Boolean(fieldError)}
            onChange={setCurrentCode}
            onComplete={(value) => void handleVerifyCurrent(undefined, value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={isSubmitting || currentCode.length !== 6}>
              {isSubmitting ? "验证中…" : "验证当前邮箱"}
            </Button>
            <button
              type="button"
              className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              disabled={isSubmitting || resendCooldown > 0}
              onClick={() => void handleSendCurrent(true)}
            >
              {resendCooldown > 0 ? `${resendCooldown} 秒后可重发` : "重新发送"}
            </button>
          </div>
        </form>
      )}

      {step === "new_email" && (
        <form className="space-y-4" onSubmit={handleSendNew}>
          <div className="space-y-2">
            <Label htmlFor="new-email">新邮箱</Label>
            <Input
              id="new-email"
              type="email"
              autoComplete="email"
              required
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <Button type="submit" disabled={isSubmitting || !newEmail.trim()}>
            {isSubmitting ? "发送中…" : "发送验证码到新邮箱"}
          </Button>
        </form>
      )}

      {step === "new_code" && (
        <form className="space-y-4" onSubmit={handleConfirmNew}>
          <p className="text-sm text-muted-foreground">请输入发送至 {newEmail} 的 6 位验证码</p>
          <OtpInput
            value={newCode}
            disabled={isSubmitting}
            invalid={Boolean(fieldError)}
            onChange={setNewCode}
            onComplete={(value) => void handleConfirmNew(undefined, value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={isSubmitting || newCode.length !== 6}>
              {isSubmitting ? "确认中…" : "确认更换"}
            </Button>
            <button
              type="button"
              className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              disabled={isSubmitting || resendCooldown > 0}
              onClick={() => {
                setFieldError(null);
                setIsSubmitting(true);
                void sendNewEmailOtp(newEmail.trim(), { force: true })
                  .then((result) => {
                    setResendCooldown(result.cooldown_seconds ?? 0);
                    toast.success(result.message || "验证码已重新发送");
                  })
                  .catch((error) => {
                    setFieldError(error instanceof ApiError ? error.message : "重新发送失败");
                  })
                  .finally(() => setIsSubmitting(false));
              }}
            >
              {resendCooldown > 0 ? `${resendCooldown} 秒后可重发` : "重新发送"}
            </button>
          </div>
        </form>
      )}

      {step === "done" && (
        <div className="space-y-3">
          <AdminAlert tone="success">邮箱已更换为 {newEmail}，本设备已自动续签登录。</AdminAlert>
          <Button type="button" variant="outline" onClick={handleClose}>
            完成
          </Button>
        </div>
      )}

      {step !== "done" ? (
        <button
          type="button"
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "text-muted-foreground")}
          onClick={handleClose}
        >
          取消改邮箱
        </button>
      ) : null}
    </div>
  );
}

function OtpInput({
  value,
  disabled,
  invalid,
  onChange,
  onComplete,
}: {
  value: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange: (value: string) => void;
  onComplete: (value: string) => void;
}) {
  return (
    <OTPField
      aria-label="6 位验证码"
      autoComplete="one-time-code"
      className="w-full justify-between"
      disabled={disabled}
      inputMode="numeric"
      length={6}
      size="lg"
      value={value}
      onValueChange={onChange}
      onValueComplete={onComplete}
    >
      <OTPFieldInput autoFocus aria-invalid={invalid || undefined} />
      <OTPFieldInput aria-invalid={invalid || undefined} />
      <OTPFieldInput aria-invalid={invalid || undefined} />
      <OTPFieldSeparator />
      <OTPFieldInput aria-invalid={invalid || undefined} />
      <OTPFieldInput aria-invalid={invalid || undefined} />
      <OTPFieldInput aria-invalid={invalid || undefined} />
    </OTPField>
  );
}
